<template>
  <modal v-model="isOpen" width="560" height="auto" :scrollable="true">
    <div class="position-relative modal-outer-container">
      <div class="modal-header border-bottom">
        <h5 class="mb-0 ml-3 pl-2 move-title">
          Move "<truncated-text :text="folder ? folder.name : ''" max="30ch" />"
        </h5>
        <button class="btn border-0" aria-label="Close" @click="close">
          <i class="fas fa-times fa-lg"></i>
        </button>
      </div>

      <div class="modal-body move-body">
        <p class="text-muted-theme small mb-3">
          Looking in
          <strong><truncated-text :text="hereLabel" max="30ch" /></strong>.
          Everything inside the folder moves with it.
        </p>

        <div class="input-group mb-3">
          <div class="input-group-prepend">
            <span class="input-group-text"><i class="fas fa-search"></i></span>
          </div>
          <input
            v-model="filter"
            type="text"
            class="form-control"
            placeholder="Filter folders"
          />
        </div>

        <alert v-if="isLoading" class="my-3" />

        <div v-else class="move-list">
          <button v-if="here" type="button" class="move-option" @click="goUp">
            <i class="fas fa-level-up-alt fa-fw mr-2 text-muted"></i>
            <span class="move-option-name"
              ><truncated-text :text="upLabel"
            /></span>
          </button>

          <button
            v-for="option in options"
            :key="option.id"
            type="button"
            class="move-option"
            :disabled="isInsideMovedFolder(option)"
            @click="drillInto(option)"
          >
            <i class="fas fa-folder fa-fw mr-2 folder-icon"></i>
            <span class="move-option-name"
              ><truncated-text :text="option.name"
            /></span>
            <i class="fas fa-chevron-right move-option-chevron"></i>
          </button>

          <p v-if="!options.length" class="move-empty mb-0">
            No folders here. "Move here" puts it at this level.
          </p>
        </div>

        <!-- Sharing is inherited, so wherever it lands decides who can see it from now on. -->
        <p v-if="inheritanceNotice" class="move-warning mt-3 mb-0">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          {{ inheritanceNotice }}
        </p>
      </div>

      <div class="modal-footer">
        <button class="btn btn-transparent mr-2" @click="close">Cancel</button>
        <save
          :saving="isMoving"
          :should-disable="!hasChanged"
          @click="submit"
          classes="btn btn-theme"
        >
          <template v-if="here"
            >Move into "<truncated-text
              :text="here.name"
              :tail="8"
              max="22ch"
            />"</template
          >
          <template v-else>Move to top level</template>
        </save>
      </div>
    </div>
  </modal>
</template>

<script>
import Swal from "sweetalert2";
import { truncateMiddle } from "@/utils/fileHelpers.js";
import { mapActions } from "vuex";

/**
 * Where a folder should live. It opens where the folder already sits, so the common case — moving
 * it beside one of its siblings — is the first thing on screen, and you can walk up to the top
 * level or down into any folder from there. "Move here" always means the level you are looking at.
 */
export default {
  name: "MoveFolderDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    folder: { type: Object, default: null },
    /** The folders walked through to reach this one — the breadcrumb, names included. */
    ancestors: { type: Array, default: () => [] },
    subjectUserId: { type: Number, default: null },
  },
  emits: ["update:modelValue", "moved"],
  data() {
    return {
      // Every folder from the top down to the one being looked at. Empty means the top level.
      trail: [],
      candidates: [],
      filter: "",
      isLoading: false,
      isMoving: false,
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },

    /** The folder being looked inside, or null at the top level. */
    here() {
      return this.trail.length ? this.trail[this.trail.length - 1] : null;
    },

    hereId() {
      return this.here ? this.here.id : null;
    },

    hereLabel() {
      return this.here ? this.here.name : "Top level";
    },

    upLabel() {
      const parent = this.trail[this.trail.length - 2];

      return parent ? `Back to ${parent.name}` : "Back to top level";
    },

    options() {
      const term = this.filter.trim().toLowerCase();
      const moving = this.folder ? this.folder.id : null;

      return this.candidates.filter(
        (c) => c.id !== moving && (!term || c.name.toLowerCase().includes(term))
      );
    },

    currentParentId() {
      return this.folder ? this.folder.folder_id || null : null;
    },

    /** Moving somewhere it already is would be a no-op the API accepts silently. */
    hasChanged() {
      return this.hereId !== this.currentParentId;
    },

    inheritanceNotice() {
      const summary = this.here && this.here.audience_summary;

      // Nothing is gained by warning about where it already lives — that move cannot happen.
      if (!this.hasChanged || !summary || !summary.count) {
        return "";
      }

      return `Everyone who can see "${truncateMiddle(this.here.name, 40)}" (${
        summary.label
      }) will be able to see this folder and everything in it.`;
    },
  },
  watch: {
    modelValue(open) {
      if (!open) {
        return;
      }

      this.filter = "";
      // Open where the folder lives. The screen that opened this already walked that path, so
      // its breadcrumb is the chain — with names, which `path` alone cannot give.
      this.trail = [...this.ancestors];
      this.load();
    },
  },
  methods: {
    ...mapActions({ moveFolder: "folders/move" }),

    /** A folder cannot be moved inside itself, and the server refuses it — so do not offer it. */
    isInsideMovedFolder(option) {
      const movingPath = this.folder && this.folder.path;

      return !!(
        movingPath &&
        option.path &&
        option.path.startsWith(movingPath)
      );
    },

    load() {
      this.isLoading = true;

      return this.$http
        .get("documents/folders", {
          params: {
            folder_id: this.hereId || undefined,
            user_id: this.hereId ? undefined : this.subjectUserId || undefined,
            sort: "name",
            direction: "asc",
            per_page: 100,
          },
        })
        .then((res) => {
          this.candidates = (res.data.data || []).filter(
            (row) => row.kind === "folder"
          );
        })
        .catch((err) => {
          if (!err || !err.accessDenied) {
            Swal.fire("Error", "Could not load the folders.", "error");
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    drillInto(option) {
      this.trail = [...this.trail, option];
      this.filter = "";
      this.load();
    },

    goUp() {
      this.trail = this.trail.slice(0, -1);
      this.filter = "";
      this.load();
    },

    submit() {
      this.isMoving = true;

      this.moveFolder({
        folderId: this.folder.id,
        targetFolderId: this.hereId,
      })
        .then((moved) => {
          if (!moved) {
            return;
          }
          this.$emit("moved");
          this.close();
        })
        .finally(() => {
          this.isMoving = false;
        });
    },

    close() {
      this.isOpen = false;
    },
  },
};
</script>

<style scoped>
.move-title {
  min-width: 0;
  font-size: 1.05rem;
}

/* Flex row: the button carrying a name has to give way rather than push past the dialog. */
.modal-footer :deep(.btn) {
  min-width: 0;
  max-width: 100%;
}

.move-body {
  /* A tint behind the list, so the white rows read as a surface rather than blending into it. */
  background: rgba(0, 0, 0, 0.025);
  max-height: 60vh;
  overflow-y: auto;
}

.move-list {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.move-option {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 0.6rem 0.9rem;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

.move-option:last-child {
  border-bottom: 0;
}

.move-option:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.035);
}

.move-option:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.move-option-name {
  flex: 1;
  /* A flex item will not shrink past its content without it, and the name overran the row. */
  min-width: 0;
}

.move-option-chevron {
  color: var(--main-text-color);
  opacity: 0.35;
  font-size: 0.8rem;
}

.folder-icon {
  color: #f5c344;
}

.move-empty {
  padding: 0.9rem;
  color: var(--main-text-color);
  opacity: 0.6;
  font-size: 0.85rem;
}

.move-warning {
  color: #b26a00;
  font-size: 0.85rem;
}
</style>
